import React, { useEffect, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import { TextField, Tooltip, Typography } from "@mui/material";

export default function TaxField({
  canEdit = true,
  decimalScale = 0,
  allowNegative = true,
  forceNegative = false,
  fixedDecimalScale = false,
  trimTrailingZeros = false,
  value,
  maxValue,
  maxValueMessage,
  onChange,
  reference,
  disabled = false,
  preserveEmpty = false,
  ...rest
}) {
  const [warning, setWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // If external value changed while editing ended, keep display in sync.
    // No-op otherwise because we read from `value` directly.
  }, [value]);

  const clearWarning = () => {
    setWarning(false);
    setWarningMessage("");
  };

  const formatWithParens = (val) => {
    if (val == null || val === "") return "";
    const n = Number(val);
    if (isNaN(n)) return "";
    const formatted = Math.abs(n).toLocaleString(undefined, {
      minimumFractionDigits: trimTrailingZeros ? 0 : decimalScale,
      maximumFractionDigits: decimalScale,
    });
    return n < 0 ? `(${formatted})` : formatted;
  };

  const internalOnValueChange = (vals) => {
    clearWarning();
    let { floatValue } = vals;

    if (floatValue == null) {
      onChange?.(preserveEmpty ? "" : 0, reference);
      return;
    }
    if (forceNegative && floatValue > 0) floatValue = -Math.abs(floatValue);

    // ✅ enforce max value (auto-set instead of block)
    if (maxValue != null && floatValue > maxValue) {
      floatValue = maxValue;
      setWarning(true);
      setWarningMessage(
        maxValueMessage || `Maximum allowed value is ${maxValue}`
      );
    }

    onChange?.(floatValue, reference);
  };

  const handleFocusDisplay = () => {
    // switch to editing mode; NumericFormat will get focus via autoFocus
    setIsEditing(true);
  };

  const handleBlurEditing = (e) => {
    // when editing blurs, ensure empty -> 0 like your old behaviour
    const txt = e?.target?.value;
    if (txt === "" || txt == null) {
      onChange?.(preserveEmpty ? "" : 0, reference);
    }
    clearWarning();
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (decimalScale === 0 && (e.key === "." || e.key === "Decimal")) {
      e.preventDefault();
      setWarning(true);
      setWarningMessage("Decimals are not allowed");
    }
  };

  const sharedSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "background.paper",
      width: "100%",
      height: "40px",
      transition: "border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
      "&:hover fieldset": {
        borderColor: "primary.main",
      },
      "&.Mui-focused fieldset": {
        borderColor: "primary.main",
        boxShadow: "0px 0px 0px 4px rgba(96, 63, 151, 0.1)",
      },
      "&.Mui-disabled": {
        backgroundColor: "background.default",
        opacity: 0.6,
        "& fieldset": {
          borderColor: "text.disabled",
        },
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
      "&.Mui-focused": {
        color: "primary.main",
      },
      "&.Mui-disabled": {
        color: "text.disabled",
      },
    },
    "& .MuiOutlinedInput-input": {
      textAlign: "right",
      "&.Mui-disabled": {
        color: "text.disabled",
        WebkitTextFillColor: "text.disabled",
      },
    },
  };

  const handleFocusEditing = (e) => {
    setIsEditing(true);

    // If current value is exactly 0, clear it out for fresh typing
    if (
      value === 0 ||
      value === "" ||
      value === null ||
      value === undefined ||
      value === "0"
    ) {
    } else {
      // move caret to start
      setTimeout(() => {
        if (e.target.setSelectionRange) {
          e.target.setSelectionRange(0, 0);
        }
      }, 0);
    }
  };

  if (!canEdit) {
    return (
      <Typography variant='body2' sx={{ fontWeight: 500 }}>
        {formatWithParens(value)}
      </Typography>
    );
  }

  return (
    <>
      {/* If editing, show the editable NumericFormat (minus sign used while editing).
          If not editing, show a readOnly TextField that displays parentheses for negatives. */}
      {isEditing ? (
        <Tooltip
          title={warning ? warningMessage : ""}
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "red",
                color: "white",
              },
            },
          }}
        >
          <NumericFormat
            // numeric editing input
            value={value ?? ""}
            decimalScale={decimalScale}
            allowNegative={allowNegative || forceNegative}
            thousandSeparator=','
            fixedDecimalScale={fixedDecimalScale}
            customInput={TextField}
            onValueChange={internalOnValueChange}
            onFocus={handleFocusEditing}
            onBlur={handleBlurEditing}
            onKeyDown={handleKeyDown}
            autoFocus
            inputRef={inputRef}
            sx={sharedSx}
            disabled={disabled}
            {...rest}
          />
        </Tooltip>
      ) : (
        <TextField
          value={formatWithParens(value)}
          onClick={handleFocusDisplay}
          onFocus={handleFocusDisplay}
          InputProps={{ readOnly: true }}
          sx={sharedSx}
          disabled={disabled}
          {...rest}
        />
      )}
    </>
  );
}
